import { Item } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect } from 'react';
import classNames from 'classnames/bind';
import withStyles from 'lib/enhancers/withStyles';
import { useI18n } from 'next-localization';
import Table, { TableProps } from 'components/helpers/Table/Table';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';

import { ComponentProps } from 'lib/component-props';
import { PlanResponse } from '../Types/GetPlanDetailsResponse.Type';
import { PlanComparisonTableFields, usePlanComparisonTable } from '../PlanComparisonTable/usePlanComparisonTable';

import styles from './PlanComparisonSARTable.module.scss';
import { SarSetupFields } from '../Types/PlanSearchTypes.Type';
import { useRouter } from 'next/router';

const cx = classNames.bind(styles);

type PlanComparisonSARTableFields = PlanComparisonTableFields & {
  sarSetup: Item;
};

type PlanComparisonSARTableProps = ComponentProps & {
  fields: PlanComparisonSARTableFields;
};

const getSarPlanClassName = (plan: PlanResponse, sarYear: string): string =>
  plan.planYear?.targetItem?.year?.value === sarYear ? 'sar-plan' : 'normal-plan';

const isEnrollAllowedForSarPlan = (plan: PlanResponse, sarYear: string): boolean =>
  plan.planYear?.targetItem?.year?.value !== sarYear;

type PlanComparisonTableViewProps = {
  isLoading: boolean;
  showNoResultsFound: boolean;
  tables: TableProps[];
};

const PlanComparisonTableView = ({ isLoading, showNoResultsFound, tables }: PlanComparisonTableViewProps) => {
  if (isLoading) {
    return (
      <div role="status">
        <span className={cx('screen-reader-only')}>Plan details loaded</span>
        <Loader variant={LoaderVariant.Spinner} />
        <br />
        <br />
      </div>
    );
  }

  if (showNoResultsFound) {
    return (
      <div role="status">
        <strong className={cx('no-plan')}>Error loading plans. Please try again at a later time.</strong>
        <br />
        <br />
      </div>
    );
  }

  return (
    <div className={cx('plan-comparison-page-wrapper')}>
      {tables.map((thistable) => (
        <div key={thistable.id} className={cx('plan-comparison-table-wrapper')}>
          <Table {...thistable} />
        </div>
      ))}
    </div>
  );
};

type PlanComparisonSARTableContentProps = {
  props: PlanComparisonSARTableProps;
  sarSetupFields: SarSetupFields;
};

const PlanComparisonSARTableContent = ({
  props,
  sarSetupFields,
}: PlanComparisonSARTableContentProps): React.JSX.Element => {
  const sarYear = sarSetupFields.originalYear.name;

  const { isLoading, tables, showNoResultsFound } = usePlanComparisonTable({
    fields: props.fields,
    getPlanClassName: (plan: PlanResponse) => getSarPlanClassName(plan, sarYear),
    isEnrollAllowedForPlan: (plan: PlanResponse) => isEnrollAllowedForSarPlan(plan, sarYear),
    planSearchPage: sarSetupFields.searchPage,
    cx,
  });

  return <PlanComparisonTableView isLoading={isLoading} tables={tables} showNoResultsFound={showNoResultsFound} />;
};

const PlanComparisonSARTable = (props: PlanComparisonSARTableProps): React.JSX.Element => {
  const { t } = useI18n();
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const updateUrlForSar = async () => {
      const regex = /^\d{4}-\d{3}(?:-\d{3})?$/;
      const isSarPlan = router.query.plan0 && regex.test(router.query.plan0 as string);

      if (!isSarPlan) {
        return;
      }

      const validParams = router.query.year && router.query.state && router.query.county;

      if (!validParams) {
        // nuke plan0 so table will route to landing page
        const params = new URLSearchParams(window.location.search);
        params.delete('plan0');
        router.replace(`${window.location.pathname}?${params.toString()}`, undefined, { shallow: true });
        return;
      }

      const origYear = (router.query.plan0 as string).substring(0, 4);
      const pbp = (router.query.plan0 as string).substring(5);

      const response = await fetch(
        `/sc-api/custom/SarLookup/GetSarPlans?` +
          `path=${window.location.pathname}` +
          `&origyear=${origYear}` +
          `&pbp=${pbp}` +
          `&upgradeyear=${router.query.year as string}` +
          `&county=${router.query.county as string}` +
          `&state=${router.query.state as string}`
      );

      const result = await response.json();

      const params = new URLSearchParams(window.location.search);
      const foundPlans = result && Object.keys(result).length > 0;

      if (foundPlans) {
        if (result.plan0) {
          params.set('plan0', result.plan0);
        }

        if (result.plan1) {
          params.set('plan1', result.plan1);
        }

        if (result.plan2) {
          params.set('plan2', result.plan2);
        }
      } else {
        params.delete('plan0');
      }

      router.replace(`${window.location.pathname}?${params.toString()}`, undefined, { shallow: true });
    };

    updateUrlForSar();
  }, [router.isReady, router.query]);

  const sarSetupFields = props.fields?.sarSetup?.fields as SarSetupFields;

  if (!sarSetupFields?.originalYear || !sarSetupFields?.searchPage || !props.fields?.planType) {
    return (
      <div className={cx('mb-4')}>
        <strong className={cx('no-plan')}>{t('PlanFailedtoLoad')}</strong>
      </div>
    );
  }

  return <PlanComparisonSARTableContent props={props} sarSetupFields={sarSetupFields} />;
};

export default withStyles()<PlanComparisonSARTableProps>(PlanComparisonSARTable);
